import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as api from '../../api';
import Input from '../Atoms/Input';
import Select from '../Atoms/Select';
import Textarea from '../Atoms/Textarea';
import BlueButton from '../Atoms/BlueButton';
import Spinner from '../Atoms/Spinner';
import Popup from '../Atoms/Popup';
import UploadProgressBar from '../Atoms/UploadProgressBar';
import { typologiesObjet, typologiesEspace } from '../Utils/Listings';
import { storeRedirect } from '../Utils/redirectUtils';

// ============================================================
// STYLED COMPONENTS
// ============================================================

const PageContainer = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Logo = styled.img`
    width: 200px;
    display: block;
    margin: 0 auto 20px;
`;

const Title = styled.h1`
    text-align: center;
    font-size: 24px;
    margin-bottom: 10px;
    color: #333;
`;

const Subtitle = styled.p`
    text-align: center;
    color: #666;
    margin-bottom: 30px;
    font-size: 15px;
`;

const WinnerBadge = styled.div`
    background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%);
    border: 1px solid #c5d3f7;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 30px;
    text-align: center;

    .project-name {
        font-size: 18px;
        font-weight: 600;
        color: #0c34c4;
        margin-bottom: 4px;
    }
    .company-name {
        font-size: 15px;
        color: #555;
        margin-bottom: 4px;
    }
    .editions {
        font-size: 13px;
        color: #888;
    }
`;

const StepSection = styled.div`
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;

    h2 {
        font-size: 18px;
        margin-bottom: 16px;
        color: #333;
    }
`;

const FormRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;

    > div {
        flex: 1;
    }

    @media (max-width: 600px) {
        flex-direction: column;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        color: #555;
        margin-bottom: 4px;
    }

    &.missing {
        border-left: 3px solid #f59e0b;
        padding-left: 12px;
        border-radius: 4px;
    }

    &.missing label::after {
        content: ' (manquant)';
        color: #f59e0b;
        font-weight: 600;
    }
`;

const MissingBanner = styled.div`
    background: #fffbeb;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 20px;
    font-size: 14px;
    color: #92400e;
`;

const SuccessMessage = styled.div`
    text-align: center;
    padding: 40px 20px;

    h2 {
        color: #059669;
        font-size: 22px;
        margin-bottom: 12px;
    }
    p {
        color: #666;
        font-size: 15px;
    }
`;

const ErrorMessage = styled.div`
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    text-align: center;
`;

const MiniatureContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
`;

const FileContainer = styled.div`
    width: 300px;

    @media (max-width: 600px) {
        width: 100%;
    }

    .mainMedia {
        width: 100%;
        height: 300px;
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        video {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .remove_cross {
            position: absolute;
            top: 8px;
            right: 8px;
            background-color: red;
            color: white;
            font-size: 18px;
            border-radius: 50%;
            padding: 2px 7px;
            opacity: 0;
            cursor: pointer;
            z-index: 2;
        }

        &:hover .remove_cross {
            opacity: 0.5;
            &:hover { opacity: 1; }
        }
    }

    .addMedia {
        width: 100%;
        height: 300px;
        background-image: linear-gradient(#fff, #bbb);
        cursor: pointer;
        color: white;
        border-radius: 8px;

        div {
            padding: 40px;
            display: flex;
            justify-content: center;
            align-items: end;
            height: 100%;
            background-image: url('/img/croix_blanche.png');
            background-repeat: no-repeat;
            background-position: center;
            background-size: 30px;
        }
    }
`;

const ProjectCard = styled.div`
    background: ${p => p.active ? '#f0f4ff' : '#f9fafb'};
    border: 2px solid ${p => p.active ? '#0c34c4' : '#e5e7eb'};
    border-radius: 10px;
    padding: 16px;
    cursor: pointer;
    margin-bottom: 12px;
    transition: all 0.2s;

    &:hover {
        border-color: #0c34c4;
    }

    .name { font-weight: 600; font-size: 16px; }
    .edition { font-size: 13px; color: #888; margin-top: 2px; }
    .status { font-size: 13px; margin-top: 6px; }
    .status.complete { color: #059669; }
    .status.incomplete { color: #f59e0b; }
`;

// ============================================================
// SUB-COMPONENTS (Media upload, reused from MyProject.js pattern)
// ============================================================

const MediaPreview = ({ media, newMedia, removeMedia }) => {
    const { i18n } = useTranslation('common');

    let file = newMedia ? media.file : media;
    if (!file || !file.type) return null;

    let fullPath, fileName, credit, legende;
    if (newMedia) {
        fullPath = URL.createObjectURL(file);
        fileName = file.name;
        credit = media.credit;
        legende = media.legende;
    } else {
        fullPath = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + file.path;
        fileName = file.fileName;
        credit = file.credit;
        legende = file.legende;
    }

    return (
        <FileContainer>
            <div className="mainMedia">
                <span className="remove_cross" onClick={() => removeMedia(media)}>✕</span>
                {file.type.includes('image') && <img src={fullPath} alt={fileName} />}
                {file.type.includes('video') && <video controls><source src={fullPath} type={file.type} /></video>}
                {file.type.includes('pdf') && <a href={fullPath} target="_blank" rel="noreferrer">{i18n.language === "fr" ? "Voir le PDF" : "See PDF"}</a>}
            </div>
            <br />
            <i>{fileName}</i><br /><br />
            <span>{i18n.language === "fr" ? "Credit" : "Copyright"} : {credit}</span><br />
            <span>{i18n.language === "fr" ? "Légende" : "Caption"} : {legende}</span><br />
        </FileContainer>
    );
};

const AddMedia = ({ medias, setMedias }) => {
    const { i18n } = useTranslation('common');
    const [popupOpen, setPopupOpen] = useState(false);
    const [currentFile, setCurrentFile] = useState(null);
    const [credit, setCredit] = useState('');
    const [legende, setLegende] = useState('');
    const fileInputRef = useRef(null);

    const handleInputChange = (e) => {
        if (e.target.files[0].size > 50000000) {
            alert(i18n.language === "fr" ? "Le fichier est trop lourd (50Mo max)." : "File too large (50MB max).");
            return;
        }
        setCurrentFile(e.target.files[0]);
    };

    const handleAddFile = (e) => {
        e.preventDefault();
        fileInputRef.current?.click();
    };

    const addMedia = (e) => {
        e.preventDefault();
        setPopupOpen(false);
        setMedias([...medias, { file: currentFile, credit, legende }]);
        setCurrentFile(null);
        setCredit('');
        setLegende('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <FileContainer>
            <div style={{ cursor: "pointer" }} onClick={() => setPopupOpen(true)} className="addMedia">
                <div>{i18n.language === 'fr' ? "Ajouter un média" : "Add a media"}</div>
            </div>
            <input hidden type='file' accept="application/pdf, image/png, image/jpeg, video/mp4, video/quicktime" onChange={handleInputChange} ref={fileInputRef} />
            {popupOpen &&
                <Popup close={() => setPopupOpen(false)}>
                    {currentFile ? (
                        <>
                            <span>{i18n.language === 'fr' ? "Fichier sélectionné :" : "Selected file:"}</span><br />
                            <a href={URL.createObjectURL(currentFile)} target="_blank" rel="noreferrer">{i18n.language === 'fr' ? 'Voir le fichier' : 'View file'}</a><br />
                            <button onClick={handleAddFile}>{i18n.language === 'fr' ? 'Changer de fichier' : 'Change file'}</button><br />
                        </>
                    ) : (
                        <>
                            <span>{i18n.language === 'fr' ? "Pas de fichier sélectionné." : "No file selected."}</span><br />
                            <button onClick={handleAddFile}>{i18n.language === 'fr' ? 'Sélectionner un fichier' : 'Select a file'}</button><br />
                        </>
                    )}
                    <span className='thin'>{i18n.language === "fr" ? "(.mp4, .pdf, .png, .jpg, 50Mo max)" : "(.mp4, .pdf, .png, .jpg, 50MB max)"}</span><br />
                    <Input style={{ width: "100%" }} onChange={e => setCredit(e.target.value)} placeholder={i18n.language === "fr" ? "Crédit" : "Copyright"} defaultValue={credit} /><br />
                    <Input style={{ width: "100%" }} onChange={e => setLegende(e.target.value)} placeholder={i18n.language === "fr" ? "Légende" : "Caption"} defaultValue={legende} /><br />
                    {currentFile && <button onClick={addMedia}>{i18n.language === 'fr' ? 'Ajouter' : 'Add'}</button>}
                </Popup>
            }
        </FileContainer>
    );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

const EDITION_LABELS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': '1ère édition',
    '417b2020-721a-4c55-bd9f-d86a913f26ad': '2ème édition',
    'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d': '3ème édition'
};

const FD100ProjectSetupPage = (props) => {
    const { i18n } = useTranslation('common');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // Page state
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Data from token validation
    const [winners, setWinners] = useState([]);
    const [hasCompany, setHasCompany] = useState(false);
    const [userCompanyId, setUserCompanyId] = useState(null);

    // Company creation (if needed)
    const [companyName, setCompanyName] = useState('');
    const [creatingCompany, setCreatingCompany] = useState(false);

    // Project selection (when multiple unique projects)
    const [selectedProjectIndex, setSelectedProjectIndex] = useState(0);

    // Project form state
    const [type, setType] = useState('objet');
    const [typologie, setTypologie] = useState('');
    const [newMedias, setNewMedias] = useState([]);
    const [mediasToDelete, setMediasToDelete] = useState([]);
    const [existingMedias, setExistingMedias] = useState([]);

    // ============================================================
    // TOKEN VALIDATION ON MOUNT
    // ============================================================

    useEffect(() => {
        if (!token) {
            setError(i18n.language === 'fr'
                ? "Lien invalide. Veuillez utiliser le lien reçu par email."
                : "Invalid link. Please use the link received by email.");
            setLoading(false);
            return;
        }

        api.ValidateFD100ProjectToken(token).then(res => {
            const data = res.data;
            setWinners(data.winners || []);
            setHasCompany(data.has_company);
            setUserCompanyId(data.user_company_id);

            // Pre-fill company name from first winner
            if (!data.has_company && data.winners?.length > 0) {
                setCompanyName(data.winners[0].company_name || '');
            }

            // Pre-fill project form from first winner/project
            if (data.winners?.length > 0) {
                const first = data.winners[0];
                if (first.existing_project) {
                    setType(first.existing_project.type || 'objet');
                    setTypologie(first.existing_project.typologie || '');
                    setExistingMedias(first.existing_project.medias || []);
                }
            }

            setLoading(false);
        }).catch(err => {
            if (err.response?.status === 401) {
                // Not logged in — store redirect and go to login
                storeRedirect('/fd100-project-setup?token=' + token);
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || (i18n.language === 'fr' ? "Erreur de validation" : "Validation error"));
            setLoading(false);
        });
    }, [token]);

    // ============================================================
    // GROUP WINNERS BY UNIQUE PROJECT
    // ============================================================

    const uniqueProjects = React.useMemo(() => {
        const groups = {};
        for (const w of winners) {
            const key = (w.project_name || '').toLowerCase().trim();
            if (!groups[key]) {
                groups[key] = {
                    project_name: w.project_name,
                    company_name: w.company_name,
                    winner_ids: [],
                    editions: [],
                    existing_project: w.existing_project,
                    project_id: w.project_id,
                    company_id: w.company_id,
                    missing_fields: w.missing_fields || []
                };
            }
            groups[key].winner_ids.push(w.id);
            groups[key].editions.push(w.edition_label);
            // Use the one that has an existing project if any
            if (w.existing_project && !groups[key].existing_project) {
                groups[key].existing_project = w.existing_project;
                groups[key].project_id = w.project_id;
                groups[key].company_id = w.company_id;
                groups[key].missing_fields = w.missing_fields;
            }
        }
        return Object.values(groups);
    }, [winners]);

    const currentProject = uniqueProjects[selectedProjectIndex] || null;

    // Update form when switching projects
    useEffect(() => {
        if (!currentProject) return;
        if (currentProject.existing_project) {
            setType(currentProject.existing_project.type || 'objet');
            setTypologie(currentProject.existing_project.typologie || '');
            setExistingMedias(currentProject.existing_project.medias || []);
        } else {
            setType('objet');
            setTypologie('');
            setExistingMedias([]);
        }
        setNewMedias([]);
        setMediasToDelete([]);
    }, [selectedProjectIndex, currentProject]);

    // ============================================================
    // COMPANY CREATION
    // ============================================================

    const handleCreateCompany = async () => {
        if (!companyName.trim()) return;
        if (!props.fullUser?.user?._id) {
            // User state not loaded yet — refresh and retry
            if (props.refreshUser) await props.refreshUser();
            if (!props.fullUser?.user?._id) {
                setError(i18n.language === 'fr' ? "Session non prête, veuillez réessayer" : "Session not ready, please retry");
                return;
            }
        }
        setCreatingCompany(true);
        try {
            const formData = new FormData();
            formData.append('name', companyName.trim());
            formData.append('userid', props.fullUser.user._id);
            await api.UpdateCompany(formData);
            if (props.refreshUser) await props.refreshUser();

            // Re-validate token to get updated company info
            const res = await api.ValidateFD100ProjectToken(token);
            setHasCompany(res.data.has_company);
            setUserCompanyId(res.data.user_company_id);
            setCreatingCompany(false);
        } catch (err) {
            setError(i18n.language === 'fr' ? "Erreur lors de la création de la société" : "Error creating company");
            setCreatingCompany(false);
        }
    };

    // ============================================================
    // PROJECT SUBMIT
    // ============================================================

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const form = e.target;
            const submission = new FormData(form);

            // If existing project, pass its ID for update; otherwise create new
            const projectId = currentProject.project_id || '';
            submission.append('projectid', projectId);
            submission.append('companyid', userCompanyId);
            submission.append('public', 'true');

            // Handle media uploads (same pattern as MyProject.js)
            submission.append('newMedias', JSON.stringify(newMedias.map((media, i) => ({
                fileIndex: i,
                fileName: media.file.name,
                credit: media.credit,
                legende: media.legende
            }))));
            submission.append('mediasToDelete', JSON.stringify(mediasToDelete));

            for (let i = 0; i < newMedias.length; i++) {
                submission.append('file_' + i, newMedias[i].file);
            }

            const onUploadProgress = (progressEvent) => {
                const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percent);
            };

            const response = await api.UpdateProject(submission, onUploadProgress);

            if (response.data === "file too big") {
                setError(i18n.language === "fr" ? "Un fichier est trop volumineux." : "A file is too big.");
                setSubmitting(false);
                return;
            }

            // Get the project ID from response (for new projects)
            const savedProjectId = response.data?.projectId || currentProject.project_id;
            const savedCompanyId = userCompanyId;

            // Link winner records to this project in Supabase
            if (savedProjectId && currentProject.winner_ids.length > 0) {
                await api.LinkFD100WinnerProject({
                    winner_ids: currentProject.winner_ids,
                    project_id: savedProjectId,
                    company_id: savedCompanyId
                });

                // Update local winners state so subsequent saves don't create duplicates
                setWinners(prev => prev.map(w =>
                    currentProject.winner_ids.includes(w.id)
                        ? { ...w, project_id: savedProjectId, company_id: savedCompanyId }
                        : w
                ));
            }

            if (props.refreshUser) props.refreshUser();

            // Check if there are more projects to fill
            if (selectedProjectIndex < uniqueProjects.length - 1) {
                setSelectedProjectIndex(selectedProjectIndex + 1);
                setSubmitting(false);
                setUploadProgress(0);
                window.scrollTo(0, 0);
            } else {
                setSuccess(true);
            }

        } catch (err) {
            console.error("Error saving project:", err);
            setError(err.response?.data?.error || (i18n.language === 'fr' ? "Erreur lors de l'enregistrement" : "Error saving"));
            setSubmitting(false);
        }
    };

    // ============================================================
    // MEDIA HELPERS
    // ============================================================

    const removeNewMedia = (media) => {
        setNewMedias(newMedias.filter(m => m !== media));
    };

    const removeOldMedia = (media) => {
        setExistingMedias(existingMedias.filter(m => m !== media));
        setMediasToDelete([...mediasToDelete, media.path]);
    };

    // ============================================================
    // RENDER
    // ============================================================

    if (loading) {
        return <PageContainer><Spinner /></PageContainer>;
    }

    if (error && !winners.length) {
        return (
            <PageContainer>
                <Logo src="/img/SD25-FD100-Logo-Story sans marge.png" alt="Le FD100" />
                <ErrorMessage>{error}</ErrorMessage>
            </PageContainer>
        );
    }

    if (success) {
        return (
            <PageContainer>
                <Logo src="/img/SD25-FD100-Logo-Story sans marge.png" alt="Le FD100" />
                <SuccessMessage>
                    <h2>{i18n.language === 'fr' ? 'Projet enregistré avec succès !' : 'Project saved successfully!'}</h2>
                    <p>{i18n.language === 'fr'
                        ? 'Votre projet lauréat est maintenant visible sur la plateforme.'
                        : 'Your winning project is now visible on the platform.'}</p>
                    <br />
                    <BlueButton onClick={() => navigate(hasCompany ? '/mycompany' : '/myprofile')}>
                        {hasCompany
                            ? (i18n.language === 'fr' ? 'Voir mon espace' : 'View my space')
                            : (i18n.language === 'fr' ? 'Enregistrer ma société' : 'Register my company')}
                    </BlueButton>
                </SuccessMessage>
            </PageContainer>
        );
    }

    const editionsList = currentProject?.editions?.join(', ') || '';
    const existingProj = currentProject?.existing_project;
    const missingFields = currentProject?.missing_fields || [];
    const isNewProject = !existingProj;

    return (
        <PageContainer>
            <Logo src="/img/SD25-FD100-Logo-Story sans marge.png" alt="Le FD100" />
            <Title>{i18n.language === 'fr' ? 'Complétez votre projet lauréat' : 'Complete your winning project'}</Title>
            <Subtitle>
                {i18n.language === 'fr'
                    ? 'Renseignez les informations de votre projet pour le rendre visible sur la plateforme Le FRENCH DESIGN.'
                    : 'Fill in your project information to make it visible on the Le FRENCH DESIGN platform.'}
            </Subtitle>

            {/* Winner badge */}
            <WinnerBadge>
                <div className="project-name">{currentProject?.project_name}</div>
                <div className="company-name">{currentProject?.company_name}</div>
                <div className="editions">Le FD100 — {editionsList}</div>
            </WinnerBadge>

            {/* Multiple projects selector */}
            {uniqueProjects.length > 1 && (
                <StepSection>
                    <h2>{i18n.language === 'fr' ? 'Vos projets lauréats' : 'Your winning projects'} ({selectedProjectIndex + 1}/{uniqueProjects.length})</h2>
                    {uniqueProjects.map((proj, i) => (
                        <ProjectCard key={i} active={i === selectedProjectIndex} onClick={() => setSelectedProjectIndex(i)}>
                            <div className="name">{proj.project_name}</div>
                            <div className="edition">{proj.editions.join(', ')}</div>
                            <div className={`status ${proj.missing_fields.length === 0 ? 'complete' : 'incomplete'}`}>
                                {proj.missing_fields.length === 0
                                    ? (i18n.language === 'fr' ? 'Projet complet' : 'Project complete')
                                    : (i18n.language === 'fr' ? 'Informations manquantes' : 'Missing information')}
                            </div>
                        </ProjectCard>
                    ))}
                </StepSection>
            )}

            {error && <ErrorMessage>{error}</ErrorMessage>}

            {/* Step 1: Company creation (if no company) */}
            {!hasCompany && (
                <StepSection>
                    <h2>{i18n.language === 'fr' ? 'Étape 1 : Créer votre société' : 'Step 1: Create your company'}</h2>
                    <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                        {i18n.language === 'fr'
                            ? 'Pour ajouter votre projet, vous devez d\'abord créer le profil de votre société.'
                            : 'To add your project, you must first create your company profile.'}
                    </p>
                    <FormGroup>
                        <label>{i18n.language === 'fr' ? 'Nom de la société' : 'Company name'}</label>
                        <Input
                            style={{ width: '100%' }}
                            value={companyName}
                            onChange={e => setCompanyName(e.target.value)}
                            placeholder={i18n.language === 'fr' ? 'Nom de votre société' : 'Your company name'}
                        />
                    </FormGroup>
                    <BlueButton onClick={handleCreateCompany} disabled={creatingCompany || !companyName.trim()}>
                        {creatingCompany
                            ? (i18n.language === 'fr' ? 'Création...' : 'Creating...')
                            : (i18n.language === 'fr' ? 'Créer la société' : 'Create company')}
                    </BlueButton>
                </StepSection>
            )}

            {/* Step 2: Project form (only if company exists) */}
            {hasCompany && (
                <form onSubmit={handleSubmit}>
                    <StepSection>
                        <h2>
                            {isNewProject
                                ? (i18n.language === 'fr' ? 'Renseigner votre projet' : 'Fill in your project')
                                : (i18n.language === 'fr' ? 'Compléter votre projet' : 'Complete your project')}
                        </h2>

                        {/* Missing fields banner for Tier C */}
                        {!isNewProject && missingFields.length > 0 && (
                            <MissingBanner>
                                {i18n.language === 'fr'
                                    ? 'Les champs suivants sont manquants : '
                                    : 'The following fields are missing: '}
                                <strong>
                                    {missingFields.map(f => {
                                        const labels = { image: 'Image', description: 'Description', typology: 'Typologie' };
                                        return labels[f] || f;
                                    }).join(', ')}
                                </strong>
                            </MissingBanner>
                        )}

                        {/* Project name */}
                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Nom du projet' : 'Project name'}</label>
                            <Input
                                name="name"
                                required
                                style={{ width: '100%' }}
                                defaultValue={existingProj?.name || currentProject?.project_name || ''}
                                placeholder={i18n.language === 'fr' ? 'Nom du projet' : 'Project name'}
                            />
                        </FormGroup>

                        {/* Type + Typology */}
                        <FormRow>
                            <FormGroup>
                                <label>{i18n.language === 'fr' ? 'Type de projet' : 'Project type'}</label>
                                <Select name="type" style={{ width: '100%' }} value={type} onChange={e => setType(e.target.value)}>
                                    <option value="objet">{i18n.language === 'fr' ? 'Objet' : 'Object'}</option>
                                    <option value="espace">{i18n.language === 'fr' ? 'Espace' : 'Space'}</option>
                                </Select>
                            </FormGroup>
                            <FormGroup className={missingFields.includes('typology') ? 'missing' : ''}>
                                <label>{i18n.language === 'fr' ? 'Typologie' : 'Typology'}</label>
                                <Select name="typologie" style={{ width: '100%' }} value={typologie} onChange={e => setTypologie(e.target.value)}>
                                    <option value="">{i18n.language === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                                    {type === 'objet' && typologiesObjet.map(t =>
                                        <option key={t.code} value={t.code}>{i18n.language === 'fr' ? t.fr : t.en}</option>
                                    )}
                                    {type === 'espace' && typologiesEspace.map(t =>
                                        <option key={t.code} value={t.code}>{i18n.language === 'fr' ? t.fr : t.en}</option>
                                    )}
                                </Select>
                            </FormGroup>
                        </FormRow>

                        {/* Year */}
                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Année de livraison' : 'Delivery year'}</label>
                            <Input
                                name="year"
                                type="number"
                                style={{ width: '200px' }}
                                defaultValue={existingProj?.year || ''}
                                placeholder="2024"
                            />
                        </FormGroup>
                    </StepSection>

                    {/* Descriptions */}
                    <StepSection>
                        <h2>{i18n.language === 'fr' ? 'Présentation du projet' : 'Project presentation'}</h2>
                        <FormGroup className={missingFields.includes('description') ? 'missing' : ''}>
                            <label>{i18n.language === 'fr' ? 'Description (Français)' : 'Description (French)'}</label>
                            <Textarea
                                name="aboutfr"
                                style={{ width: '100%', minHeight: '120px' }}
                                defaultValue={existingProj?.aboutfr || ''}
                                placeholder={i18n.language === 'fr' ? 'Présentation du projet en français...' : 'Project description in French...'}
                            />
                        </FormGroup>
                        <FormGroup>
                            <label>{i18n.language === 'fr' ? 'Description (Anglais)' : 'Description (English)'}</label>
                            <Textarea
                                name="abouten"
                                style={{ width: '100%', minHeight: '120px' }}
                                defaultValue={existingProj?.abouten || ''}
                                placeholder={i18n.language === 'fr' ? 'Présentation du projet en anglais...' : 'Project description in English...'}
                            />
                        </FormGroup>
                    </StepSection>

                    {/* Medias */}
                    <StepSection>
                        <h2 style={missingFields.includes('image') ? { color: '#f59e0b' } : {}}>
                            {i18n.language === 'fr' ? 'Médias' : 'Media'}
                            {missingFields.includes('image') && <span style={{ fontSize: 13, fontWeight: 400 }}> ({i18n.language === 'fr' ? 'manquant' : 'missing'})</span>}
                        </h2>
                        <MiniatureContainer>
                            <AddMedia medias={newMedias} setMedias={setNewMedias} />
                            {newMedias.map((media, index) =>
                                <MediaPreview key={index} media={media} newMedia removeMedia={removeNewMedia} />
                            )}
                            {existingMedias.map((media) =>
                                <MediaPreview key={media.path} media={media} removeMedia={removeOldMedia} />
                            )}
                        </MiniatureContainer>
                    </StepSection>

                    {/* Submit */}
                    {uploadProgress > 0 && uploadProgress < 100 && (
                        <UploadProgressBar progress={uploadProgress} />
                    )}
                    <div style={{ textAlign: 'center', marginTop: 20 }}>
                        <BlueButton type="submit" disabled={submitting}>
                            <img src='/img/fleche_blanche.png' alt='' />
                            {submitting
                                ? (i18n.language === 'fr' ? 'Enregistrement...' : 'Saving...')
                                : (i18n.language === 'fr' ? 'Enregistrer mon projet' : 'Save my project')}
                        </BlueButton>
                    </div>
                </form>
            )}
        </PageContainer>
    );
};

export default FD100ProjectSetupPage;
